import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import bravaxLogo from '@/assets/bravaxforms-logo.png';
import {
  BarChart3,
  Users,
  Zap,
  CreditCard,
  Activity,
  Mail,
  LogOut,
  Moon,
  Sun,
  X,
  ShieldCheck,
  ChevronLeft,
} from 'lucide-react';

export type AdminTab = 'overview' | 'users' | 'usage' | 'payments' | 'health' | 'broadcast';

interface AdminSidebarProps {
  activeTab: AdminTab;
  onTabChange: (tab: AdminTab) => void;
  open: boolean;
  onClose: () => void;
}

const navGroups = [
  {
    label: 'Dashboard',
    items: [
      { id: 'overview' as AdminTab, name: 'Overview', icon: BarChart3 },
      { id: 'users' as AdminTab, name: 'Users', icon: Users },
      { id: 'usage' as AdminTab, name: 'AI & Usage', icon: Zap },
    ],
  },
  {
    label: 'Operations',
    items: [
      { id: 'payments' as AdminTab, name: 'Payments', icon: CreditCard },
      { id: 'health' as AdminTab, name: 'System Health', icon: Activity },
      { id: 'broadcast' as AdminTab, name: 'Broadcast', icon: Mail },
    ],
  },
];

const AdminSidebar = ({ activeTab, onTabChange, open, onClose }: AdminSidebarProps) => {
  const navigate = useNavigate();
  const { resolvedTheme, toggleTheme } = useTheme();

  const handleLogout = async () => {
    sessionStorage.removeItem('bravax_admin_session');
    await supabase.auth.signOut();
    navigate('/admin-login', { replace: true });
  };

  const handleTabClick = (tab: AdminTab) => {
    onTabChange(tab);
    onClose();
  };

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'sidebar fixed top-0 left-0 w-[260px] h-screen bg-card text-foreground border-r border-border z-40 transition-all duration-300 lg:translate-x-0 overflow-y-auto flex flex-col',
          open ? 'translate-x-0 shadow-xl' : '-translate-x-full'
        )}
      >
        {/* Logo Section (matches sidebar width) */}
        <div className="w-full h-16 flex items-center justify-between px-5 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <img src={bravaxLogo} alt="Bravax Forms" className="w-6 h-6 object-contain dark:invert" />
            <span className="font-semibold text-foreground text-lg tracking-tight">Admin Shell</span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors lg:hidden"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav py-5 flex-1">
          {navGroups.map((group) => (
            <div key={group.label}>
              <div className="category-label px-6 py-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mt-2">
                <span className="category-text">{group.label}</span>
              </div>
              <div className="space-y-0.5 mt-1">
                {group.items.map((item) => {
                  const isActive = activeTab === item.id;
                  return (
                    <div className="menu-link relative" key={item.id}>
                      <button
                        onClick={() => handleTabClick(item.id)}
                        className={cn(
                          'w-full h-11 flex items-center px-6 transition-colors duration-200 group',
                          isActive
                            ? 'bg-muted text-primary'
                            : 'text-foreground hover:bg-muted focus:bg-muted active:bg-muted'
                        )}
                      >
                        <item.icon
                          className={cn(
                            'w-5 h-5 flex-shrink-0 transition-colors',
                            isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                          )}
                        />
                        <span className="menu-text flex-1 text-left text-sm font-medium ml-3 truncate">
                          {item.name}
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Bottom User Area */}
        <div className="border-t border-border mt-auto">
          <div className="p-4 flex items-center gap-3 border-b border-border">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center ring-2 ring-border">
              <ShieldCheck className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm truncate">Administrator</h3>
              <p className="text-xs text-muted-foreground truncate">Bravax Technologies</p>
            </div>
          </div>
          <div className="py-2">
            <button
              onClick={toggleTheme}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              {resolvedTheme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              <span>{resolvedTheme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Back to App</span>
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Log out</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;
